/**
 * The draft, as the UI sees it.
 *
 * Every rule lives in the engine; this holds the session state around it —
 * which squad is on screen, which player is waiting for a batting position,
 * how many re-spins are left — and re-derives the offers whenever any of that
 * changes.
 *
 * The two phases are the engine's, not this file's. Nothing here decides when
 * the bench opens; `phaseOf(state)` does, and the UI reads it.
 */

import {
  createDraft,
  evaluateSquad,
  pickInto,
  phaseOf,
  validate,
  createRng,
  DEFAULT_RULES,
  type DraftState,
  type DraftPhase,
  type Offer,
  type Rng,
  type Validation,
} from '@ipl-sim/engine-cricket';
import type { Card, Roster, Squad } from './roster';
import { track } from './analytics';

/**
 * Free re-spins. Beyond this the plan offers a rewarded ad, hard-capped at
 * three for everyone and never the only way to earn one — competitive modes
 * are seed-locked so a re-spin cannot buy a better draw.
 */
export const FREE_RESPINS = 3;

export const FIRST_YEAR = 2008;

/** A batting slot as the UI renders it: the position, and who is in it. */
export interface SlotView {
  readonly slot: number;
  readonly player: Card | null;
  /** True while a chosen player is waiting for a position and could go here. */
  readonly isLegalTarget: boolean;
}

/** A requirement chip in the draft header. */
export interface Requirement {
  readonly label: string;
  readonly met: boolean;
}

export class Draft {
  /**
   * Assigned in the constructor, which runs after every field initializer
   * below. The placeholder is what makes that legal to a typechecker: the
   * `$derived` fields close over `this.#roster` but do not read it until
   * something reads them, by which time the constructor has finished.
   */
  #roster: Roster = undefined!;
  #rng: Rng = undefined!;

  /** The seed every spin comes from. Recorded so a draft can be replayed. */
  readonly seed: string;

  fromYear = $state(FIRST_YEAR);
  toYear = $state(FIRST_YEAR);

  engine = $state<DraftState>(createDraft());
  squadIndex = $state(0);
  /** Set only while a multi-position player waits for the user to pick a slot. */
  selected = $state<Card | null>(null);
  respinsLeft = $state(FREE_RESPINS);
  teamName = $state('');

  constructor(roster: Roster, seed: string = crypto.randomUUID()) {
    this.#roster = roster;
    this.seed = seed;
    this.#rng = createRng(seed);
    this.fromYear = roster.years[0];
    this.toYear = roster.years[roster.years.length - 1];
    this.squadIndex = 0;
  }

  // ── what the engine says ───────────────────────────────────────────────

  readonly squads = $derived<readonly Squad[]>(this.#roster.squadsIn(this.fromYear, this.toYear));
  readonly pool = $derived<readonly Card[]>(this.#roster.poolIn(this.fromYear, this.toYear));

  readonly squad = $derived<Squad>(this.squads[this.squadIndex % this.squads.length]);
  readonly phase = $derived<DraftPhase>(phaseOf(this.engine));

  /**
   * Recomputed on every pick and every spin. Measured at 8.2 ms over the full
   * 2008–2026 pool and 1.8 ms for a single season, which is why this can be a
   * plain derived value rather than something scheduled off the main thread.
   */
  readonly offers = $derived<readonly Offer[]>(
    evaluateSquad(this.engine, this.squad.squad, this.pool),
  );

  // ── what the screen renders ────────────────────────────────────────────

  readonly xi = $derived<readonly SlotView[]>(
    Array.from({ length: DEFAULT_RULES.xiSize }, (_, i) => {
      const slot = i + 1;
      const player = (this.engine.xi.get(slot) as Card | undefined) ?? null;
      return {
        slot,
        player,
        isLegalTarget: this.selected !== null && this.#legalSlotsFor(this.selected).includes(slot),
      };
    }),
  );

  readonly subs = $derived<readonly (Card | null)[]>(
    Array.from(
      { length: DEFAULT_RULES.impactSubs },
      (_, i) => (this.engine.impactSubs[i] as Card | undefined) ?? null,
    ),
  );

  readonly picked = $derived<readonly Card[]>([...this.engine.xi.values()] as Card[]);

  /**
   * The header chips. Bowling depth counts `bowlOvers`, never the role label:
   * the source data types R Ashwin, Sam Curran and Glenn Maxwell as "BAT", and
   * trusting it leaves some squads unable to cover twenty overs.
   */
  readonly requirements = $derived<readonly Requirement[]>(
    this.phase === 'XI'
      ? [
          {
            label: `${this.picked.filter((p) => !p.isIndian).length}/${DEFAULT_RULES.maxOverseas} overseas`,
            met: this.picked.filter((p) => !p.isIndian).length <= DEFAULT_RULES.maxOverseas,
          },
          {
            label: this.picked.some((p) => p.role === 'WK') ? 'Keeper ✓' : 'Keeper —',
            met: this.picked.some((p) => p.role === 'WK'),
          },
          {
            label: `Bowlers ${this.picked.filter((p) => p.bowlOvers >= 1).length}/${DEFAULT_RULES.recommendedBowlers}`,
            met:
              this.picked.filter((p) => p.bowlOvers >= 1).length >= DEFAULT_RULES.recommendedBowlers,
          },
        ]
      : [
          { label: 'Indian players only', met: true },
          { label: 'Any season, any role', met: true },
        ],
  );

  readonly count = $derived(
    this.phase === 'XI'
      ? { have: this.engine.xi.size, need: DEFAULT_RULES.xiSize }
      : { have: this.engine.impactSubs.length, need: DEFAULT_RULES.impactSubs },
  );

  readonly isComplete = $derived(this.phase === 'DONE');
  readonly validation = $derived<Validation>(validate(this.engine));

  /**
   * Whether anything on the current squad can be taken.
   *
   * Often false late in a draft, and that is normal — the feasibility guard
   * promises a legal completion exists somewhere in the *pool*, never that the
   * fifteen players currently on screen contain one of them.
   */
  readonly hasPickable = $derived(this.offers.some((o) => !o.blockedReason));

  /** Offers are positional; the squad list renders in batting order. */
  readonly rows = $derived<readonly Offer[]>(
    [...this.offers].sort(
      (a, b) => a.player.posMin - b.player.posMin || a.player.posMax - b.player.posMax,
    ),
  );

  // ── actions ────────────────────────────────────────────────────────────

  /**
   * Take a player. One legal position assigns immediately; more than one opens
   * the slot bar. During the bench phase there is no position to choose at all.
   */
  choose(offer: Offer): void {
    if (offer.blockedReason) return;

    if (offer.canBeImpact) {
      this.engine = pickInto(this.engine, offer.player, { kind: 'IMPACT' });
      this.#trackPick(offer.player as Card, 0);
      this.selected = null;
      this.#spin();
      return;
    }

    if (offer.legalSlots.length === 1) {
      this.assign(offer.player as Card, offer.legalSlots[0]);
      return;
    }
    this.selected = offer.player as Card;
  }

  assign(player: Card, slot: number): void {
    this.engine = pickInto(this.engine, player, { kind: 'XI', slot });
    this.#trackPick(player, slot);
    this.selected = null;
    this.#spin();
  }

  /**
   * Drop-off by pick number is the most actionable chart this project has, so
   * the pick number matters more than anything else recorded here. Slot 0 means
   * the bench, which has no batting order.
   */
  #trackPick(player: Card, slot: number): void {
    track('player_picked', {
      pickNumber: this.engine.xi.size + this.engine.impactSubs.length,
      slot,
      rating: Math.round((player.bat + player.pow + player.bowl) / 3),
      isCapWinner: player.isOrangeCap || player.isPurpleCap,
      isOverseas: !player.isIndian,
      role: player.role,
    });
  }

  cancelPick(): void {
    this.selected = null;
  }

  /**
   * Move to another squad. Picking a player also advances the squad, so this
   * is only for walking away from one — which is why it is the limited
   * resource.
   *
   * Leaving a squad with nothing pickable is FREE, and must be. The
   * feasibility guard guarantees a legal completion exists in the pool, not
   * that it exists among the fifteen players on screen, so dead squads are a
   * normal part of a draft — charging for them lets a user run out of
   * re-spins in front of a squad they cannot pick from, which is precisely
   * the stranded draft the whole guard exists to prevent.
   */
  respin(): boolean {
    const wasPickable = this.hasPickable;
    if (wasPickable) {
      if (this.respinsLeft <= 0) return false;
      this.respinsLeft--;
    }
    // 'free' distinguishes a dead squad the user was let off from a re-spin
    // they actually spent, which is the difference between the guard doing its
    // job and the allowance being too tight.
    track('respin_used', {
      source: wasPickable ? 'token' : 'free',
      tokensLeft: this.respinsLeft,
    });
    this.selected = null;
    this.#spin();
    return true;
  }

  setRange(from: number, to: number): void {
    this.fromYear = from;
    this.toYear = to;
    this.squadIndex = 0;
    this.selected = null;
    this.#spin();
  }

  reset(): void {
    this.engine = createDraft();
    this.selected = null;
    this.respinsLeft = FREE_RESPINS;
    this.teamName = '';
    this.#rng = createRng(this.seed);
    this.#spin();
  }

  // ── internals ──────────────────────────────────────────────────────────

  /**
   * A fresh squad. Drawn from the seeded RNG rather than Math.random so the
   * whole draft replays from `(seed, picks)` — the same property the match
   * relies on for server-side verification.
   */
  #spin(): void {
    const n = this.squads.length;
    if (n <= 1) return;
    // Never show the same squad twice in a row: it reads as a broken button.
    let next = this.squadIndex;
    while (next === this.squadIndex) next = this.#rng.int(n);
    this.squadIndex = next;
    track('spin', {
      pickNumber: this.engine.xi.size + this.engine.impactSubs.length,
      squadLabel: this.squads[next].label,
    });
  }

  #legalSlotsFor(player: Card): readonly number[] {
    return this.offers.find((o) => o.player.id === player.id)?.legalSlots ?? [];
  }
}
